import React, { FunctionComponent, useCallback, useEffect, useState } from 'react';

import cn from 'classnames';
import { useSelector } from 'react-redux';

import { Button, ModalConfirm } from '../../../../shared/components';
import { Popover } from '../../../../shared/components/popover';
import SvgImage from '../../../../shared/components/svg-image';
import { i18n } from '../../../../shared/helpers/i18n';
import { countMetrika } from '../../../../shared/helpers/metrika';
import { AccessLevel } from '../../../../types';
import { selectIsWaitingRoomEnabled, selectIsWaitingRoomOverflow } from '../../selectors';
import css from './index.css';

const ButtonCheckmarkIcon = <SvgImage className={css.CheckmarkIcon} imageName="checkmark" />;
const publicIcon = <SvgImage className={css.AccessLevelIcon} imageName="insecurity" />;
const privateIcon = <SvgImage className={css.AccessLevelIcon} imageName="security" />;

type AccessLevelControlProps = {
    className?: string;
    isActive?: boolean;
    currentAccessLevel?: AccessLevel;
    shouldShowPopup?: boolean;
    onClosePopup?(): void;
    onMainButtonClick?(): void;
    onAccessLevelChange(level: AccessLevel): void;
};

interface AccessLevelOption {
    value: AccessLevel;
    content: string;
    icon?: React.JSX.Element;
    testId?: string;
}
const DEFAULT_ACCESS_LEVELS = (): AccessLevelOption[] => [
    {
        value: AccessLevel.ORGANIZATION,
        content: i18n('moderation', 'access-level-organization'),
        icon: privateIcon,
        testId: 'access-level-organization-button',
    },
    {
        value: AccessLevel.PUBLIC,
        content: i18n('moderation', 'access-level-public'),
        icon: publicIcon,
        testId: 'access-level-public-button',
    },
];

const WAITING_ROOM_ACCESS_LEVELS = (): AccessLevelOption[] => [
    {
        value: AccessLevel.ADMINS,
        content: i18n('moderation', 'access-level-waiting-room-admins'),
        testId: 'access-level-waiting-room-admins-button',
    },
    {
        value: AccessLevel.ORGANIZATION,
        content: i18n('moderation', 'access-level-waiting-room-organization'),
        testId: 'access-level-waiting-room-organization-button',
    },
    {
        value: AccessLevel.PUBLIC,
        content: i18n('moderation', 'access-level-waiting-room-public'),
        testId: 'access-level-waiting-room-public-button',
    },
];

const METRIKA_NAMESPACE = ['waiting_rooms', 'conference_private_level'];

const ACCESS_LEVEL_METRIKA: Record<AccessLevel, string> = {
    [AccessLevel.ADMINS]: 'open_for_admin',
    [AccessLevel.ORGANIZATION]: 'open_for_organisation',
    [AccessLevel.PUBLIC]: 'open_for_all',
};

const AccessLevelControl: FunctionComponent<AccessLevelControlProps> = ({
    className,
    isActive,
    currentAccessLevel,
    shouldShowPopup,
    onClosePopup,
    onMainButtonClick,
    onAccessLevelChange,
}) => {
    const popupRef = React.useRef<HTMLDivElement>(null);
    const buttonRef = React.useRef<HTMLDivElement>(null);

    const isWaitingRoomEnabled = useSelector(selectIsWaitingRoomEnabled);
    const isWaitingRoomOverflow = useSelector(selectIsWaitingRoomOverflow);

    const options = isWaitingRoomEnabled ? WAITING_ROOM_ACCESS_LEVELS() : DEFAULT_ACCESS_LEVELS();
    const currentValue = currentAccessLevel || AccessLevel.PUBLIC;
    const currentOption = options.find(({ value }) => value === currentValue);
    const buttonAccessTitle = currentOption?.content;
    const buttonAccessIcon = currentOption?.icon;
    const buttonTestId = currentOption?.testId;

    const [isConfirmVisible, setIsConfirmVisible] = useState(false);
    const onConfirmClose = useCallback(() => {
        countMetrika(...METRIKA_NAMESPACE, 'limit_attention_popup_cancelled');
        setIsConfirmVisible(false);
    }, []);
    const onConfirm = useCallback(() => {
        countMetrika(...METRIKA_NAMESPACE, 'limit_attention_popup_accepted');
        setIsConfirmVisible(false);
        onAccessLevelChange(AccessLevel.ORGANIZATION);
    }, [onAccessLevelChange]);

    const onSelectChange = useCallback(
        (value: AccessLevel) => {
            const metrika = ACCESS_LEVEL_METRIKA[value];
            countMetrika(...METRIKA_NAMESPACE, metrika);
            if (
                isWaitingRoomEnabled &&
                isWaitingRoomOverflow &&
                currentAccessLevel === AccessLevel.ADMINS &&
                value === AccessLevel.ORGANIZATION
            ) {
                countMetrika(...METRIKA_NAMESPACE, 'limit_attention_popup_showed');
                setIsConfirmVisible(true);
                onClosePopup?.();
            } else {
                onAccessLevelChange(value);
            }
        },
        [onAccessLevelChange, currentAccessLevel, isWaitingRoomEnabled, isWaitingRoomOverflow, onClosePopup]
    );

    useEffect(() => {
        if (shouldShowPopup) {
            countMetrika(...METRIKA_NAMESPACE, 'list_open');
        }
    }, [shouldShowPopup]);

    const isButtonHidden = isWaitingRoomEnabled && !isActive;

    return (
        <div className={cn(className, { [css.Disabled]: !isActive })}>
            {!isButtonHidden && (
                <div ref={buttonRef}>
                    <Button
                        view="neutral-transparent"
                        size="lg"
                        iconLeft={buttonAccessIcon}
                        onClick={onMainButtonClick}
                        className={css.buttonAccess}
                        testId={buttonTestId}
                    >
                        {buttonAccessTitle}
                        {isActive ? (
                            <SvgImage
                                className={cn(css.AccessLevelIconDropdown, {
                                    [css.AccessLevelIconDropdownActive]: shouldShowPopup,
                                })}
                                imageName="selectArrow"
                            />
                        ) : null}
                    </Button>
                </div>
            )}

            {isActive ? (
                <Popover
                    preventInteractOutside={false}
                    ref={popupRef}
                    placement="bottom left"
                    isOpen={shouldShowPopup}
                    triggerRef={buttonRef}
                    className={css.Popover}
                    onOpenChange={onClosePopup}
                    offset={5}
                >
                    <div className={cn(css.Container, { [css.waitingRoom]: isWaitingRoomEnabled })}>
                        {options.map((option) => (
                            <div className={css.Row} key={option.value}>
                                <Button
                                    className={css.Option}
                                    view="static-light-transparent"
                                    size="md"
                                    iconLeft={option.icon}
                                    onClick={() => onSelectChange(option.value)}
                                    testId={option.testId}
                                >
                                    {option.content}
                                </Button>
                                {currentAccessLevel === option.value ? ButtonCheckmarkIcon : null}
                            </div>
                        ))}
                    </div>
                </Popover>
            ) : null}
            {isConfirmVisible ? (
                <ModalConfirm
                    onClose={onConfirmClose}
                    onConfirm={onConfirm}
                    headerText={i18n('moderation', 'modal-confirm-access-level-limit-title')}
                    cancelButtonText={i18n('common', 'cancel')}
                    confirmButtonText={i18n('common', 'got-it')}
                >
                    {i18n('moderation', 'modal-confirm-access-level-limit-text')}
                </ModalConfirm>
            ) : null}
        </div>
    );
};

export default AccessLevelControl;
